// Status webhooks arrive out of order, so delivery only moves forward along this chain.
const deliveryChain = ['queued', 'sending', 'sent', 'delivered', 'read'];
const failures = ['undelivered', 'failed', 'delivery_unknown'];

const rank = status => deliveryChain.indexOf(status);

const isFailure = status => failures.includes(status);

// The backend clears `error` exactly when a resend leaves, so an explicit null is the only
// proof that a `sent` belongs to a retry and not to a webhook that arrived late.
const clearsError = incoming => Boolean(incoming)
  && Object.prototype.hasOwnProperty.call(incoming, 'error')
  && incoming.error === null;

// Status-only merge for the writes that never expose the sibling `error`: a failed message is
// handed over untouched, since only `error` says whether it may go back into the chain.
export const mergeStatusChain = (existing, incoming) => {
  if (!existing) return incoming;
  if (!incoming) return existing;
  if (isFailure(existing)) return incoming;
  // A failure only lands on a message that is not delivered yet — the rule the API applies in
  // Message#accepts_status? — so the chat cannot get stuck on a failure the database already undid.
  if (isFailure(incoming)) return rank(existing) >= rank('delivered') ? existing : incoming;
  return rank(incoming) > rank(existing) ? incoming : existing;
};

// Full merge, for the writes that carry the whole message: `status` and `error` are decided together.
const mergeMessageStatus = (existingStatus, incoming) => {
  const incomingStatus = incoming && incoming.status;
  if (!incomingStatus) return existingStatus;
  if (!existingStatus) return incomingStatus;

  if (isFailure(existingStatus)) {
    // A failure is only left on proof: a delivery receipt, or the resend that cleared the error.
    if (rank(incomingStatus) >= rank('delivered')) return incomingStatus;
    if (rank(incomingStatus) >= rank('sent') && clearsError(incoming)) return incomingStatus;
    return existingStatus;
  }
  return mergeStatusChain(existingStatus, incomingStatus);
};

export default mergeMessageStatus;
